import { useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import type { ProceduralTimelineEvent } from '../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { formatDate } from '../procedural-timeline/components/procedural-helpers';
import { DateTbdLabel } from '../procedural-timeline/components/procedural-row-parts';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { type CarouselApi, Carousel, CarouselContent, CarouselItem } from '@/new/components/shadcn/carousel';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { cn } from '@/new/lib/utils';

interface ProceduralTimelineWidgetProps {
  matterId: string;
}

export function ProceduralTimelineWidget({ matterId }: ProceduralTimelineWidgetProps) {
  const { data: events, isLoading } = useGetProceduralTimelineEvents(matterId);

  const sorted = useMemo(() => sortByStart(events ?? []), [events]);
  // Land on the first event that's "today or later", falling back to the last
  // event when everything's in the past so the carousel still opens on
  // something meaningful.
  const defaultIndex = useMemo(() => findUpcomingIndex(sorted), [sorted]);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <Header matterId={matterId} />
      <div className="bg-white p-5 pt-4">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : sorted.length === 0 ? (
          <Empty className="rounded-lg border border-dashed border-gray-200 py-6">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--calendar-clock" />
              </EmptyMedia>
              <EmptyTitle className="text-base">No Events Yet</EmptyTitle>
              <EmptyDescription>
                Upload a court order or timetable to extract procedural events, or open the timeline to add one
                manually.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <EventCarousel events={sorted} startIndex={defaultIndex} matterId={matterId} />
        )}
      </div>
    </div>
  );
}

function Header({ matterId }: { matterId: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b bg-gray-50 px-4 py-2.5">
      <div className="flex items-center gap-3">
        <Icon className="lucide--calendar-clock h-4 w-4 text-gray-500" />
        <span className="font-medium text-gray-700">Procedural Timeline</span>
        <NewChip className="relative top-auto right-auto -ml-1" />
      </div>
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/matters/${matterId}/procedural-timeline`}>
          Open timeline
          <Icon className="lucide--arrow-right" />
        </Link>
      </Button>
    </div>
  );
}

interface EventCarouselProps {
  events: ProceduralTimelineEvent[];
  startIndex: number;
  matterId: string;
}

function EventCarousel({ events, startIndex, matterId }: EventCarouselProps) {
  // A single event has nothing to cycle: show it full width with no controls so
  // the chrome doesn't imply there's more to see.
  const isSingle = events.length <= 1;
  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(startIndex);

  useEffect(() => {
    if (!api) return;
    const sync = () => setSelected(api.selectedScrollSnap());
    sync();
    api.on('select', sync).on('reInit', sync);
    return () => {
      api.off('select', sync).off('reInit', sync);
    };
  }, [api]);

  return (
    <Carousel
      setApi={setApi}
      // One event at a time: full-width slides, no peek of the neighbours.
      opts={{ align: 'center', startIndex, watchDrag: !isSingle }}
      className="flex flex-col gap-3"
    >
      <div className="flex items-stretch gap-2">
        {api?.canScrollPrev() === true && <ArrowButton direction="prev" onClick={() => api.scrollPrev()} />}
        {/* Explicit flex-1 wrapper: CarouselContent's viewport (which embla measures) fills this. */}
        <div className="min-w-0 flex-1">
          <CarouselContent className="ml-0">
            {events.map((e, i) => (
              <CarouselItem key={e.id} className="basis-full pl-0">
                <Link
                  to={`/matters/${matterId}/procedural-timeline`}
                  tabIndex={i === selected ? 0 : -1}
                  aria-hidden={i !== selected}
                  className={cn(
                    'group relative flex items-center justify-between gap-3 overflow-hidden rounded-md bg-white p-3 px-4 transition-all',
                    isEventPast(e) && 'opacity-60',
                  )}
                >
                  <div className="flex min-w-0 flex-1">
                    <EventSummary event={e} />
                  </div>
                  <Icon className="lucide--arrow-right mr-1 shrink-0 text-teal-500 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" />
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
        </div>
        {api?.canScrollNext() === true && <ArrowButton direction="next" onClick={() => api.scrollNext()} />}
      </div>
      {!isSingle && <Indicator total={events.length} activeIndex={selected} onSelect={(i) => api?.scrollTo(i)} />}
    </Carousel>
  );
}

function EventSummary({ event }: { event: ProceduralTimelineEvent }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="flex items-center gap-2 text-base font-medium text-gray-900">
        {event.startDateTime !== null ? (
          <span className="text-gray-500">{formatDate(event.startDateTime)}</span>
        ) : (
          <DateTbdLabel />
        )}
        <span className="text-sm text-gray-400/60">•</span>
        <span className="truncate">{event.name}</span>
      </p>
      {event.description && <p className="line-clamp-1 text-xs text-gray-500">{event.description}</p>}
    </div>
  );
}

function ArrowButton({ direction, onClick }: { direction: 'prev' | 'next'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'prev' ? 'Previous event' : 'Next event'}
      className="shrink-0 self-center px-1 text-gray-400 transition-colors hover:text-gray-700"
    >
      <Icon className={direction === 'prev' ? 'lucide--chevron-left' : 'lucide--chevron-right'} />
    </button>
  );
}

function Indicator({
  total,
  activeIndex,
  onSelect,
}: {
  total: number;
  activeIndex: number;
  onSelect: (i: number) => void;
}) {
  // Cap the dot count so a long timeline doesn't sprawl across the widget.
  const MAX_DOTS = 12;
  const dotCount = Math.min(total, MAX_DOTS);
  // Map the active row index onto a dot index proportionally when truncated.
  const activeDot = total <= MAX_DOTS ? activeIndex : Math.floor((activeIndex / (total - 1)) * (dotCount - 1));
  return (
    <div className="flex items-center justify-center gap-1.5">
      {Array.from({ length: dotCount }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(total <= MAX_DOTS ? i : Math.round((i / (dotCount - 1)) * (total - 1)))}
          aria-label={`Go to event ${i + 1} of ${dotCount}`}
          className={cn(
            'h-1.5 w-1.5 rounded-full transition-colors',
            i === activeDot ? 'bg-teal-500' : 'bg-gray-300 hover:bg-gray-400',
          )}
        />
      ))}
    </div>
  );
}

function sortByStart(events: ProceduralTimelineEvent[]): ProceduralTimelineEvent[] {
  return events.slice().sort((a, b) => {
    const aTime = a.startDateTime !== null ? new Date(a.startDateTime).getTime() : Number.POSITIVE_INFINITY;
    const bTime = b.startDateTime !== null ? new Date(b.startDateTime).getTime() : Number.POSITIVE_INFINITY;
    return aTime - bTime;
  });
}

function findUpcomingIndex(events: ProceduralTimelineEvent[]): number {
  if (events.length === 0) return 0;
  const now = Date.now();
  const idx = events.findIndex((e) => {
    if (e.startDateTime === null) return false;
    return new Date(e.startDateTime).getTime() >= now;
  });
  return idx === -1 ? events.length - 1 : idx;
}

function isEventPast(event: ProceduralTimelineEvent): boolean {
  if (event.startDateTime === null) return false;
  return new Date(event.startDateTime).getTime() < Date.now();
}
